/**
 * Structural Steel Discipline Configuration
 */

export const FIELD_MAP = {
  buildingNumber: 'building_number',
  area: 'area',
  subArea: 'sub_area',
  elevation: 'elevation',
  floor: 'floor',
  equipmentTagOrCode: 'equipment_tag_or_code',
  description: 'description',
  budgetedHr: 'budgeted_hr',
  credit: 'credit',
  costCode: 'cost_code',
  sovCode: 'sov_code',
  sovDescription: 'sov_description',
  receiveShakeOut: 'receive_n_shake_out',
  layoutDrill: 'layout_n_drill',
  erect: 'erect',
  boltUp: 'bolt_up',
  paintTouchUp: 'paint_touch_up',
  drawingNumber: 'drawing_number',
  pAndId: 'p_and_id',
  length: 'length',
  weight: 'weight',
  divisionArea: 'division_area',
  subDivisionArea: 'sub_division_area',
  activityId: 'activity_id',
  activityName: 'activity_name',
  inter: 'inter',
  comments: 'comments',
  udf01: 'udf01',
  udf02: 'udf02',
  udf03: 'udf03',
  udf04: 'udf04',
  udf05: 'udf05',
  udf06: 'udf06',
  udf07: 'udf07',
  udf08: 'udf08',
  udf09: 'udf09',
  udf10: 'udf10',
};

export const FIELD_COMPLETION_COLUMNS = [
  'receiveShakeOut',
  'layoutDrill',
  'erect',
  'boltUp',
  'paintTouchUp',
];

export const EDITABLE_COLUMNS = [
  'activityId', 'activityName',
  'buildingNumber', 'area', 'subArea', 'elevation', 'floor',
  'equipmentTagOrCode', 'description', 'budgetedHr', 'credit', 'costCode', 'sovCode',
  'receiveShakeOut', 'layoutDrill', 'erect', 'boltUp', 'paintTouchUp',
  'drawingNumber', 'pAndId', 'length', 'weight', 'divisionArea', 'subDivisionArea', 'inter',
  'comments', 'udf01', 'udf02', 'udf03', 'udf04', 'udf05',
  'udf06', 'udf07', 'udf08', 'udf09', 'udf10',
];

export const transformMatrixData = (matrixData) => {
  return matrixData.map((row, index) => ({
    id: row.id || index,
    area: row.area || '',
    subArea: row.sub_area || '',
    buildingNumber: row.building_number || '',
    floor: row.floor || '',
    divisionArea: row.division_area || '',
    subDivisionArea: row.sub_division_area || '',
    jobNumber: row.job_number || '',
    activityId: row.activity_id || '',
    activityName: row.activity_name || '',
    costCode: row.cost_code || '',
    sovCode: row.sov_code || '',
    sovDescription: row.sov_description || '',
    elevation: row.elevation || '',
    pAndId: row.p_and_id || '',
    drawingNumber: row.drawing_number || '',
    equipmentTagOrCode: row.equipment_tag_or_code || '',
    description: row.description || '',
    length: row.length ? Number(row.length) : 0,
    weight: row.weight ? Number(row.weight) : 0,
    inter: row.inter || '',
    receiveShakeOut: row.receive_n_shake_out ? Math.round(row.receive_n_shake_out) : 0,
    layoutDrill: row.layout_n_drill ? Math.round(row.layout_n_drill) : 0,
    erect: row.erect ? Math.round(row.erect) : 0,
    boltUp: row.bolt_up ? Math.round(row.bolt_up) : 0,
    paintTouchUp: row.paint_touch_up ? Math.round(row.paint_touch_up) : 0,
    totalFieldCompletion: row.total_field_completion ? Math.round(row.total_field_completion) : 0,
    budgetedHr: row.budgeted_hr ? Number(row.budgeted_hr) : 0,
    credit: row.credit ? Number(row.credit) : 0,
    earnedHr: row.earned_hr ? Number(row.earned_hr) : 0,
    earnedCredit: row.earned_credit ? Number(row.earned_credit) : 0,
    createdAt: row.created_at || '',
    updatedAt: row.updated_at || '',
    comments: row.comments || '',
    udf01: row.udf01 || '',
    udf02: row.udf02 || '',
    udf03: row.udf03 || '',
    udf04: row.udf04 || '',
    udf05: row.udf05 || '',
    udf06: row.udf06 || '',
    udf07: row.udf07 || '',
    udf08: row.udf08 || '',
    udf09: row.udf09 || '',
    udf10: row.udf10 || '',
  }));
};

export const buildChangePayload = (rowId, columnId, oldValue, newValue) => {
  const backendField = FIELD_MAP[columnId];
  if (!backendField) return null;
  return { id: rowId, changes: { [backendField]: newValue } };
};

export const structuralConfig = {
  id: 'structural',
  name: 'Structural Steel',
  menuId: 'structure',
  tableName: 'structural_steel_tracking',
  historyTableName: 'structural_tracking_history',
  
  api: {
    getData: '/discipline-matrix/structural/project/{projectId}',
    saveChanges: '/discipline-matrix/structural/save-changes',
    export: '/discipline-matrix/structural/export/{projectId}',
    upload: '/discipline-matrix/structural/upload',
    priorities: '/priorities',
    columnMappings: '/discipline-matrix/structural/project/{projectId}/column-mappings',
  },
  
  // Permission requirements (pt_* = Progress Tracking module)
  permissions: {
    edit: 'pt_edit',
    analytics: 'pt_analytics',
    import: 'pt_import',
    export: 'pt_export',
    template: 'pt_template',
    columnConfig: 'pt_columnconfig',
    roc: 'pt_roc',
    sfRatio: 'pt_sfratio',
    matBore: 'pt_matbore',
    delete: 'pt_delete',
  },
  
  columns: [
    // Dashboard columns - full names with text wrapping
    { id: 'id', header: 'ID', accessor: 'id', dbField: 'id', width: 50, editable: false, filterable: true, type: 'number', showInDashboard: true },
    { id: 'area', header: 'Area', accessor: 'area', dbField: 'area', width: 80, editable: true, filterable: true, type: 'text', showInDashboard: true },
    { id: 'subArea', header: 'Sub Area', accessor: 'subArea', dbField: 'sub_area', width: 80, editable: true, filterable: true, type: 'text', showInDashboard: true },
    { id: 'buildingNumber', header: 'Building Number', accessor: 'buildingNumber', dbField: 'building_number', width: 80, editable: true, filterable: true, type: 'text', showInDashboard: true },
    { id: 'floor', header: 'Floor', accessor: 'floor', dbField: 'floor', width: 60, editable: true, filterable: true, type: 'text', showInDashboard: true },
    { id: 'divisionArea', header: 'Division Area', accessor: 'divisionArea', dbField: 'division_area', width: 80, editable: true, filterable: true, type: 'text', showInDashboard: true },
    { id: 'subDivisionArea', header: 'Sub Division Area', accessor: 'subDivisionArea', dbField: 'sub_division_area', width: 80, editable: true, filterable: true, type: 'text', showInDashboard: true },
    { id: 'jobNumber', header: 'Job Number', accessor: 'jobNumber', dbField: 'job_number', width: 80, editable: false, filterable: true, type: 'text', showInDashboard: true },
    { id: 'activityId', header: 'Activity ID', accessor: 'activityId', dbField: 'activity_id', width: 130, editable: true, filterable: true, type: 'text', showInDashboard: true, headerGroup: 'Schedule' },
    { id: 'activityName', header: 'Activity Name', accessor: 'activityName', dbField: 'activity_name', width: 160, editable: true, filterable: true, type: 'text', showInDashboard: false, headerGroup: 'Schedule' },
    { id: 'costCode', header: 'Cost Code', accessor: 'costCode', dbField: 'cost_code', width: 80, editable: true, filterable: true, type: 'text', showInDashboard: true },
    { id: 'sovCode', header: 'SOV Code', accessor: 'sovCode', dbField: 'sov_code', width: 80, editable: true, filterable: true, type: 'text', showInDashboard: true },
    { id: 'elevation', header: 'Elevation', accessor: 'elevation', dbField: 'elevation', width: 70, editable: true, filterable: true, type: 'text', showInDashboard: true },
    { id: 'pAndId', header: 'P&ID', accessor: 'pAndId', dbField: 'p_and_id', width: 70, editable: true, filterable: true, type: 'text', showInDashboard: true },
    { id: 'drawingNumber', header: 'Drawing Number', accessor: 'drawingNumber', dbField: 'drawing_number', width: 80, editable: true, filterable: true, type: 'text', showInDashboard: true },
    { id: 'equipmentTagOrCode', header: 'Equipment Tag/Code', accessor: 'equipmentTagOrCode', dbField: 'equipment_tag_or_code', width: 90, editable: true, filterable: true, type: 'text', showInDashboard: true },
    { id: 'description', header: 'Description', accessor: 'description', dbField: 'description', width: 120, editable: true, filterable: true, type: 'text', showInDashboard: true },
    { id: 'length', header: 'Length', accessor: 'length', dbField: 'length', width: 65, editable: true, filterable: true, type: 'number', showInDashboard: true },
    { id: 'weight', header: 'Weight', accessor: 'weight', dbField: 'weight', width: 65, editable: true, filterable: true, type: 'number', showInDashboard: true },
    { id: 'inter', header: 'Inter', accessor: 'inter', dbField: 'inter', width: 60, editable: true, filterable: true, type: 'text', showInDashboard: true },
    // Progress columns
    { id: 'receiveShakeOut', header: 'Receive & Shake Out %', accessor: 'receiveShakeOut', dbField: 'receive_n_shake_out', width: 75, editable: true, filterable: true, type: 'progress', options: [0, 25, 50, 75, 100], affectsCalculation: 'totalFieldCompletion', headerColor: '#FFF3E0', headerGroup: 'Field Progress', showInDashboard: true },
    { id: 'layoutDrill', header: 'Layout & Drill %', accessor: 'layoutDrill', dbField: 'layout_n_drill', width: 70, editable: true, filterable: true, type: 'progress', options: [0, 25, 50, 75, 100], affectsCalculation: 'totalFieldCompletion', headerColor: '#FFF3E0', headerGroup: 'Field Progress', showInDashboard: true },
    { id: 'erect', header: 'Erect %', accessor: 'erect', dbField: 'erect', width: 65, editable: true, filterable: true, type: 'progress', options: [0, 25, 50, 75, 100], affectsCalculation: 'totalFieldCompletion', headerColor: '#FFF3E0', headerGroup: 'Field Progress', showInDashboard: true },
    { id: 'boltUp', header: 'Bolt Up %', accessor: 'boltUp', dbField: 'bolt_up', width: 65, editable: true, filterable: true, type: 'progress', options: [0, 25, 50, 75, 100], affectsCalculation: 'totalFieldCompletion', headerColor: '#FFF3E0', headerGroup: 'Field Progress', showInDashboard: true },
    { id: 'paintTouchUp', header: 'Paint Touch Up %', accessor: 'paintTouchUp', dbField: 'paint_touch_up', width: 70, editable: true, filterable: true, type: 'progress', options: [0, 25, 50, 75, 100], affectsCalculation: 'totalFieldCompletion', headerColor: '#FFF3E0', headerGroup: 'Field Progress', showInDashboard: true },
    { id: 'totalFieldCompletion', header: 'Total Field Completion %', accessor: 'totalFieldCompletion', dbField: 'total_field_completion', width: 75, editable: false, filterable: true, type: 'progress-readonly', computed: true, headerColor: '#FFF3E0', headerGroup: 'Field Progress', showInDashboard: true },
    // Hidden from dashboard
    { id: 'budgetedHr', header: 'Budgeted Hr', accessor: 'budgetedHr', dbField: 'budgeted_hr', width: 90, editable: true, filterable: true, type: 'number', showInDashboard: false },
    { id: 'credit', header: 'Credit', accessor: 'credit', dbField: 'credit', width: 70, editable: true, filterable: true, type: 'number', showInDashboard: false },
    { id: 'earnedHr', header: 'Earned Hr', accessor: 'earnedHr', dbField: 'earned_hr', width: 90, editable: false, filterable: true, type: 'number', computed: true, showInDashboard: false },
    { id: 'earnedCredit', header: 'Earned Credit', accessor: 'earnedCredit', dbField: 'earned_credit', width: 100, editable: false, filterable: true, type: 'number', computed: true, showInDashboard: false },
    { id: 'createdAt', header: 'Created At', accessor: 'createdAt', dbField: 'created_at', width: 130, editable: false, filterable: true, type: 'datetime', showInDashboard: false },
    { id: 'updatedAt', header: 'Updated At', accessor: 'updatedAt', dbField: 'updated_at', width: 130, editable: false, filterable: true, type: 'datetime', showInDashboard: false },
    { id: 'comments', header: 'Comments', accessor: 'comments', dbField: 'comments', width: 120, editable: true, filterable: true, type: 'text', showInDashboard: false },
  ],
  
  udfColumns: Array.from({ length: 10 }, (_, i) => ({
    id: `udf${String(i + 1).padStart(2, '0')}`,
    header: `UDF ${String(i + 1).padStart(2, '0')}`,
    accessor: `udf${String(i + 1).padStart(2, '0')}`,
    dbField: `udf${String(i + 1).padStart(2, '0')}`,
    width: 100, editable: true, filterable: true, type: 'text', isUdf: true,
  })),
  
  fieldMap: FIELD_MAP,
  editableColumns: EDITABLE_COLUMNS,
  transformData: transformMatrixData,
  buildChangePayload: buildChangePayload,
  
  features: {
    dragFill: true,
    progressTrends: true,
    matBore: false,
    rulesOfCredit: true,
    columnConfig: true,
    actualSpentUpload: false,
    export: true,
    upload: true,
    presence: true,
  },
};

export default structuralConfig;

